import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import AssetsIcon from '@mui/icons-material/DocumentScanner';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, Grid, IconButton, Input, Typography } from "@mui/material";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useEffect, useState } from "react";
import { HEADER_HEIGHT, PRIMARY_COLOUR } from "../constants";
import Light from "../Refinery/Lights/Light";
import { MeshManager } from "../Refinery/MeshManager";
import { RenderObject } from "../Refinery/Objects/RenderObject";
import { ObjectType } from "../Refinery/Objects/types";
import { TextureManager } from "../Refinery/TextureManager";
import { Asset, ASSET_TYPE, AssetRequestMetadata } from "../types/types";
import { Controls, SelectedUpdateData } from "./Controls";
import { AddObjectModal } from "./Modals/AddObjectModal";
import { AssetsModal } from "./Modals/AssetsModal";
import { SceneSettingsData, SceneSettingsModal } from "./Modals/SceneSettingsModal";


enum ModalState {
  ADD_MODAL,
  SCENE_SETTINGS,
  ASSETS,
}

export type InspectorProps = {
  open: boolean;
  objects: { [key: string]: RenderObject };
  lights: { [key: string]: Light };
  sceneData: SceneSettingsData;
  isLoggedIn: boolean;
  textureManager?: TextureManager;
  meshManager?: MeshManager;
  selectedObject?: RenderObject | Light;
  assets?: Asset[];

  onAssetUpload: (assetId: string, metadata: AssetRequestMetadata) => void;
  onAssetDelete: (asset: Asset) => void;
  onSceneSettingsChange: (data: SceneSettingsData) => void;
  onSelect: (value: RenderObject | Light | undefined) => void;
  onAdd: (type: ObjectType | "light", name: string, meshKey?: string) => void;
  onSelectedChange: (d: SelectedUpdateData) => void;
  onSelectedClone: () => void;
  onSelectedDelete: () => void;
  onSceneDownload: () => void;
  onSceneUpload: (data: string) => void;
  onSelectedRename: (newName: string, type: "object" | "light") => void;
};

export const Inspector = (props: InspectorProps) => {
  const [modalControl, setModalControl] = useState<ModalState | undefined>(undefined);
  const [newObjectName, setNewObjectName] = useState<string>(props.selectedObject?.name ?? "");
  const [editingName, setEditingName] = useState<boolean>(false);

  const canRenameSelected = (): boolean => {
    if (newObjectName === props.selectedObject?.name) {
      return true;
    }

    if (props.selectedObject instanceof RenderObject) {
      return props.objects[newObjectName] === undefined;
    } else if (props.selectedObject instanceof Light) {
      return props.lights[newObjectName] === undefined;
    }

    return true;
  }

  useEffect(() => {
    if (props.selectedObject)
      setNewObjectName(props.selectedObject?.name);
  }, [props.selectedObject])

  return (
    <Grid id="inspector-drawer">
      <AddObjectModal
        open={modalControl === ModalState.ADD_MODAL}
        onClose={() => setModalControl(undefined)}
        meshKeys={props.meshManager?.listMeshKeys() ?? []}
        onAdd={(type, name, meshKey) => {
          props.onAdd(type, name, meshKey);
          setModalControl(undefined);
        }}
      />
      <SceneSettingsModal
        open={modalControl === ModalState.SCENE_SETTINGS}
        onClose={() => setModalControl(undefined)}
        onDownload={props.onSceneDownload}
        onUpload={props.onSceneUpload}
        onSceneSettingsChange={props.onSceneSettingsChange}
        data={props.sceneData}
      />
      <AssetsModal
        open={modalControl === ModalState.ASSETS}
        onClose={() => setModalControl(undefined)}
        assets={props.assets?.filter(a => [ASSET_TYPE.MESH_DATA, ASSET_TYPE.TEXTURE_DATA].includes(a.type))}
        onUpload={async (assetId, metadata) => await props.onAssetUpload(assetId, metadata)}
        onDelete={props.onAssetDelete}
      />
      <Grid
        width='100%'
        container
        id="inspector-drawer-root"
        height="100%"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          minHeight: '100vh',
        })}
      >
        <Grid size={12} height="100%" container spacing={1}>
          <Grid
            height="100%"
            id="scene-settings-root"
            size={props.selectedObject ? 4 : 12}
          >
            <Grid
              size={12}
              height={HEADER_HEIGHT}
              alignContent='center'
              textAlign='center'
              sx={(theme) => ({
                backgroundColor: PRIMARY_COLOUR,
                borderTopLeftRadius: theme.shape.borderRadius,
                borderTopRightRadius: theme.shape.borderRadius,
              })}
            >
              <Typography color="textPrimary" variant='h6'>Scene</Typography>
            </Grid>
            <Grid size={12}>
              <Typography color="textPrimary">Objects</Typography>
            </Grid>
            <Grid size={12}>
              <List>
                {Object.keys(props.objects).map(k => {
                  return (
                    <ListItem
                      key={k}
                      disablePadding
                    >
                      <ListItemButton
                        selected={k === props.selectedObject?.name}
                        onClick={() => {
                          props.onSelect(props.objects[k]);
                        }}>
                        <ListItemText
                          sx={(theme) => ({
                            color: theme.palette.text.secondary,
                          })}
                          primary={k}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>
            </Grid>
            <Grid size={12}>
              <Typography color="textPrimary">Lights</Typography>
            </Grid>
            <Grid size={12}>
              <List>
                {Object.keys(props.lights).map(k => {
                  return (
                    <ListItem
                      key={k}
                      disablePadding
                    >
                      <ListItemButton
                        selected={k === props.selectedObject?.name}
                        onClick={() => {
                          props.onSelect(props.lights[k]);
                        }}>
                        <ListItemText
                          primary={k}
                          sx={(theme) => ({
                            color: theme.palette.text.secondary,
                          })}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>
            </Grid>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="stretch"
              gap={2}
              sx={{
                width: 'fit-content', // only as wide as the widest button
              }}
            >
              <Button
                onClick={() => setModalControl(ModalState.ADD_MODAL)}
                variant="contained"
                startIcon={<AddIcon />}
              >
                Add
              </Button>

              <Button
                onClick={() => setModalControl(ModalState.SCENE_SETTINGS)}
                variant="outlined"
                startIcon={<SettingsIcon />}
              >
                Scene Settings
              </Button>

              {props.isLoggedIn && (
                <Button
                  onClick={() => setModalControl(ModalState.ASSETS)}
                  variant="outlined"
                  startIcon={<AssetsIcon />}
                >
                  Assets
                </Button>
              )}
            </Box>
          </Grid>
          <Grid
            id="object-settings-root"
            size={props.selectedObject ? 8 : 0}
          >
            <Grid
              size={12}
              height={HEADER_HEIGHT}
              alignContent='center'
              textAlign='center'
              container
              sx={(theme) => ({
                backgroundColor: PRIMARY_COLOUR,
                borderTopRightRadius: theme.shape.borderRadius,
                borderTopLeftRadius: theme.shape.borderRadius,
              })}
            >
              {/* Allow this to be renamed? */}
              {!editingName ? (
                <>
                  <Grid size={10}>
                    <Typography color="textPrimary" variant='h6'>{props.selectedObject?.name}</Typography>
                  </Grid>
                  <Grid size={2}>
                    <IconButton onClick={() => {
                      setEditingName(true);
                    }}>
                      <EditIcon />
                    </IconButton>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid size={10}>
                    <Input
                      value={newObjectName}
                      onChange={(e) => {
                        setNewObjectName(e.target.value);
                      }}
                    />
                  </Grid>
                  <Grid size={2}>
                    <IconButton
                      disabled={!canRenameSelected()}
                      onClick={() => {
                        setEditingName(false);
                        if (newObjectName === props.selectedObject?.name) {
                          return;
                        }

                        // set the new name
                        if (props.selectedObject instanceof RenderObject) {
                          props.onSelectedRename(newObjectName, "object");
                        } else {
                          props.onSelectedRename(newObjectName, "light");
                        }
                      }}>
                      <CheckIcon />
                    </IconButton>
                  </Grid>
                </>
              )}

            </Grid>
            <Grid>
              <Controls
                textures={props.textureManager?.listTextures() ?? []}
                sceneRenderObjects={Object.values(props.objects)}
                selectedObject={props.selectedObject}
                onSelectedChange={props.onSelectedChange}
                onSelectedClone={props.onSelectedClone}
                onSelectedDelete={props.onSelectedDelete}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
